import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import SimpleMessageDialog from "./SimpleMessageDialog";
import SimpleConfirmDialog from "./SimpleConfirmDialog";
import moment from "moment";
import { useIdleTimer } from "react-idle-timer";
import { userActions, appActions } from "../actions";
import userService from "../services/user.service";
import { ConnectingAirportsOutlined } from "@mui/icons-material";

const SessionExpireDialog = props => {
  const { user, app, refreshApp, logout, history, refreshToken } = props;

  const SESSION_PROMPT_TIME = 5;
  const IDLE_THRESHOLD = 10;
  const PROMPT_TIMEOUT = 4;

  const [open, setOpen] = useState(false);
  const [isIdle, setIdle] = useState(false);
  const [showExtendSessionPrompt, setShowExtendSessionPrompt] = useState(false);
  const [showFinalWarning, setShowFinalWarning] = useState(false);
  const promptTimerId = useRef(null);
  const hasPromptedRef = useRef(false);
  const [countdown, setCountdown] = useState(PROMPT_TIMEOUT * 60);
  const countdownIntervalRef = useRef(null);

  const getSessionEndTime = () =>
    user.auth?.expire_time ? moment(user.auth.expire_time) : null;

  const handleExtendSession = useCallback(() => {
    const sessionEndTime = getSessionEndTime();
    const now = moment();
    if (!sessionEndTime || now.isAfter(sessionEndTime)) {
      setShowExtendSessionPrompt(false);
      setOpen(true);
      logout();
      return;
    }

    refreshToken()
      .then(() => {
        setShowExtendSessionPrompt(false);
        hasPromptedRef.current = false;

        if (promptTimerId.current) {
          clearTimeout(promptTimerId.current);
          promptTimerId.current = null;
        }
      })
      .catch(err => {
        if (err.message === "Refresh token max extension reached") {
          setShowFinalWarning(true);
        } else {
          setOpen(true);
        }
      });
  }, [refreshToken, user.auth?.expire_time]);

  const handleDeclineExtension = () => {
    setShowExtendSessionPrompt(false);
    setOpen(true);
  };

  const handleCloseReminderPopUp = () => {
    setShowExtendSessionPrompt(false);
    userService.revokeToken();
    logout();
    history.push("/");
  };

  const handleClose = () => {
    refreshApp();
    logout();
    history.push("/");
  };

  const handleIdle = ({ idle }) => {
    setIdle(idle);
  };

  useEffect(() => {
    const sessionEndTime = getSessionEndTime();
    if (!sessionEndTime) return;

    const interval = setInterval(() => {
      const now = moment();
      const minutesLeft = sessionEndTime.diff(now, "minutes");
      if (sessionEndTime.isBefore(now)) {
        setOpen(true);
        return;
      }

      if (
        minutesLeft <= SESSION_PROMPT_TIME &&
        minutesLeft > 0 &&
        !hasPromptedRef.current
      ) {
        hasPromptedRef.current = true;
        if (isIdle) {
          setShowExtendSessionPrompt(true);
        } else {
          handleExtendSession();
        }
      }
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [user.auth?.expire_time, isIdle, showFinalWarning, handleExtendSession]);

  useEffect(() => {
    if (showExtendSessionPrompt) {
      setCountdown(PROMPT_TIMEOUT * 60);
      countdownIntervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            handleDeclineExtension();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [showExtendSessionPrompt]);

  
  useEffect(() => {
    const err = user?.error || app?.error;
    if (!err) return;

    const code = Number(err?.code ?? err?.status ?? err?.statusCode);
    const combined = `${err?.message ?? ""} ${err?.error_description ?? ""} ${err?.error ?? ""}`.toLowerCase();

    const startsWithInvalidToken = /^invalid token\b/.test(combined);
    const containsInvalidTokenUnderscore = combined.includes("invalid_token");

    if (code === 401 && (startsWithInvalidToken || containsInvalidTokenUnderscore)) {
      setOpen(true);
    }
  }, [
    user?.error?.code,
    user?.error?.status,
    user?.error?.statusCode,
    user?.error?.message,
    user?.error?.error_description,
    user?.error?.error,

    app?.error?.code,
    app?.error?.status,
    app?.error?.statusCode,
    app?.error?.message,
    app?.error?.error_description,
    app?.error?.error
  ]);


  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const sessionEndTime = getSessionEndTime();
        if (!sessionEndTime || moment().isAfter(sessionEndTime)) {
          setShowExtendSessionPrompt(false);
          setOpen(true);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user.auth?.expire_time]);

  const sessionEndTime = getSessionEndTime();

  const onActive = () => {
    setIdle(false);
  };

  const onIdle = () => {
    setIdle(true);
  };
  

  useIdleTimer({
    timeout: IDLE_THRESHOLD * 60 * 1000,
    // timeout: (1000  * 15),
    onIdle: onIdle,
    onActive: onActive
  });


  return (
    <>
      {/* {user.auth && (
        <Idle timeout={IDLE_THRESHOLD * 60 * 1000} onChange={handleIdle} />
      )}
 */}
      <SimpleConfirmDialog
        name="extend-session-dialog"
        title="You have been idle for 10 mins, would you like to keep the session active?"
        description={`Session will expire in ${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, "0")}`}
        closeButtonText="No"
        okButtonText="Yes"
        closeHandler={handleCloseReminderPopUp}
        okHandler={handleExtendSession}
        open={showExtendSessionPrompt}
      />

      <SimpleMessageDialog
        type="none"
        name="final-warning"
        title="Session Ending Soon"
        description={`Please save / submit your works as you have reach max time allowed. Session will end on ${sessionEndTime ? moment(sessionEndTime).format("HH:mm") : ""
          }`}
        closeButtonText="Close"
        closeHandler={() => {
          setShowFinalWarning(false);
          setShowExtendSessionPrompt(false);
        }}
        open={showFinalWarning}
      />

      <SimpleMessageDialog
        type="none"
        name="timeout-dialog"
        title="Session Expired"
        description="Please login again."
        closeButtonText="OK"
        closeHandler={handleClose}
        open={open}
      />
    </>
  );
};

const mapStateToProps = state => ({
  user: state.user,
  app: state.app
});

const mapDispatchToProps = dispatch => ({
  refreshApp: () => dispatch(appActions.setError({})),
  logout: () => dispatch(userActions.logout()),
  refreshToken: () => dispatch(userActions.updateRefreshToken())
});

export default connect(mapStateToProps, mapDispatchToProps)(SessionExpireDialog);
